import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import type { SceneOptions } from './types'
import { ClippingManager } from './ClippingManager'

export interface ExternalRenderer {
  render: (renderer: THREE.WebGLRenderer, scene: THREE.Scene) => void
  dispose: () => void
}

export class SceneManager {
  public scene: THREE.Scene
  public camera: THREE.PerspectiveCamera
  public renderer: THREE.WebGLRenderer
  public controls?: OrbitControls
  public clipping: ClippingManager

  private animationId: number | null = null
  private clock = new THREE.Clock()
  private frameCallbacks: ((delta: number, elapsed: number) => void)[] = []
  private externalRenderers: ExternalRenderer[] = []
  private initialCameraPosition = new THREE.Vector3()
  private initialControlsTarget = new THREE.Vector3()
  private frameCount = 0

  constructor(options: SceneOptions) {
    const {
      canvas,
      backgroundColor = 0x1a1a2e,
      cameraPosition = { x: 5, y: 5, z: 5 },
      cameraFov = 60,
      cameraNear = 0.1,
      cameraFar = 1000,
      enableOrbit = true,
    } = options

    this.scene = new THREE.Scene()
    this.scene.background = new THREE.Color(backgroundColor)

    const cw = canvas.clientWidth || 800
    const ch = canvas.clientHeight || 600

    this.camera = new THREE.PerspectiveCamera(cameraFov, cw / ch, cameraNear, cameraFar)
    this.camera.position.set(cameraPosition.x, cameraPosition.y, cameraPosition.z)
    this.camera.lookAt(0, 0, 0)

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true })
    this.renderer.setSize(cw, ch)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 1.0
    this.renderer.outputColorSpace = THREE.SRGBColorSpace

    this.clipping = new ClippingManager(this.scene, this.renderer)

    if (enableOrbit) {
      this.controls = new OrbitControls(this.camera, canvas)
      this.controls.enableDamping = true
      this.controls.dampingFactor = 0.12
      this.controls.update()
    }

    this.initialCameraPosition.copy(this.camera.position)
    if (this.controls) {
      this.initialControlsTarget.copy(this.controls.target)
    }
  }

  start() {
    this.clock.start()
    this.tick()
  }

  stop() {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }
  }

  onFrame(cb: (delta: number, elapsed: number) => void): () => void {
    this.frameCallbacks.push(cb)
    return () => {
      this.frameCallbacks = this.frameCallbacks.filter(fn => fn !== cb)
    }
  }

  addExternalRenderer(er: ExternalRenderer) {
    this.externalRenderers.push(er)
  }

  removeExternalRenderer(er: ExternalRenderer) {
    this.externalRenderers = this.externalRenderers.filter(e => e !== er)
  }

  add(obj: THREE.Object3D) {
    this.scene.add(obj)
  }

  remove(obj: THREE.Object3D) {
    this.scene.remove(obj)
  }

  resize(w?: number, h?: number) {
    const width = w ?? this.renderer.domElement.clientWidth
    const height = h ?? this.renderer.domElement.clientHeight
    this.camera.aspect = width / height
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(width, height)
  }

  saveCurrentViewAsReset() {
    this.initialCameraPosition.copy(this.camera.position)
    if (this.controls) {
      this.initialControlsTarget.copy(this.controls.target)
    }
  }

  resetView() {
    this.camera.position.copy(this.initialCameraPosition)
    if (this.controls) {
      this.controls.target.copy(this.initialControlsTarget)
      this.controls.update()
    }
  }

  fitToObjects(objects: THREE.Object3D[], padding = 1.5) {
    if (objects.length === 0) return
    const box = new THREE.Box3()
    for (const obj of objects) {
      box.expandByObject(obj)
    }
    if (box.isEmpty()) return
    const center = box.getCenter(new THREE.Vector3())
    const size = box.getSize(new THREE.Vector3())
    const maxDim = Math.max(size.x, size.y, size.z)
    if (this.controls) {
      this.controls.target.copy(center)
    }
    const dist = maxDim * padding + 2
    this.camera.position.set(center.x + dist * 0.6, center.y + dist * 0.4, center.z + dist)
    this.camera.lookAt(center)
    if (this.controls) {
      this.controls.update()
    }
  }

  dispose() {
    this.stop()
    this.controls?.dispose()
    this.clipping.dispose()
    for (const er of this.externalRenderers) {
      er.dispose()
    }
    this.externalRenderers = []
    this.renderer.dispose()
    this.scene.traverse(obj => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose()
        if (Array.isArray(obj.material)) {
          obj.material.forEach(m => m.dispose())
        } else {
          obj.material.dispose()
        }
      }
    })
  }

  private tick = () => {
    this.animationId = requestAnimationFrame(this.tick)
    const delta = this.clock.getDelta()
    const elapsed = this.clock.getElapsedTime()
    this.frameCount++

    this.controls?.update()
    this.frameCallbacks.forEach(cb => cb(delta, elapsed))

    this.renderer.render(this.scene, this.camera)

    if (this.frameCount % 2 === 0) {
      for (const er of this.externalRenderers) {
        er.render(this.renderer, this.scene)
      }
    }
  }
}
